class VariableInterpreterConstants
{
    ELEMENT_ID_PREFIX: string;
    PLACEHOLDER_ATTR_NAME: string;
}


class VariableInterpreter {
    private static _instance: VariableInterpreter = null;

    public constants: VariableInterpreterConstants = {
        ELEMENT_ID_PREFIX: 'VariableInterpreterElement_',
        // NOTE: contains the replacement element’s ID
        PLACEHOLDER_ATTR_NAME: 'data-placeholder',
    };

    private _elements: Object = null;

    constructor() {
        if (VariableInterpreter._instance) {
            throw new Error("Cannot instantiate...Use getInstance method instead");
        }
        VariableInterpreter._instance = this;

        this._elements = {};
    }

    public static getInstance(): VariableInterpreter {
        if (VariableInterpreter._instance === null) {
            VariableInterpreter._instance = new VariableInterpreter();
        }
        return VariableInterpreter._instance;
    }

    public interpret(valueString: string, variables: Object, bibItem: IBookInfo): string {
        if (typeof valueString === 'undefined' || typeof bibItem === 'undefined') {
            console.log("VariableInterpreter: cannot interpret undefined type");
            return "";
        }
        return this.interpretPattern("bibItem", valueString, variables, bibItem, true, "");
    }

    public replacePlaceholders(div: HTMLDivElement)
    {
        const attrName = this.constants.PLACEHOLDER_ATTR_NAME;
        const idPrefix = this.constants.ELEMENT_ID_PREFIX;

        const placeholders = div.querySelectorAll(
            `div[${attrName} ^= "${idPrefix}"]`);

        placeholders.forEach(placeholder =>
        {
            const elementId = placeholder.getAttribute(attrName);
            const element = this._elements[elementId];

            if (!element) {
                console.warn(elementId, 'doesn’t exist on', this._elements);
                return;
            }

            placeholder.replaceWith(element);
        });
    }

    public deletePlaceholders()
    {
        const keys = Object.keys(this._elements);

        for (let key of keys) {
            delete this._elements[key];
        }
    }

    private resolveScope(interpretedVariable: Object, scopedObject: Object): Object {
        var scope: string = interpretedVariable["scope"];
        var actualScopedObject: Object = scopedObject;
        if (typeof scope !== 'undefined') {
            if (scope === "{$parent}") {
                actualScopedObject = this.getParentScope(actualScopedObject);
            } else {
                actualScopedObject = this.changeToInnerScope(actualScopedObject, scope);
            }
        }
        return actualScopedObject;
    }

    private changeToInnerScope(actualScope: Object, newScopeName: string): Object {
        var newScope: Object = actualScope[newScopeName];
        this.setParentScope(newScope, actualScope);
        return newScope;
    }

    private changeToOuterScope(actualScope: Object): Object {
        return this.getParentScope(actualScope);
    }

    private setParentScope(scope: Object, parentScope: Object) {
        if (typeof scope === 'undefined' || scope === null) {
            console.log("cannot set parent scope to undefined or null scope");
            return;
        }
        scope['parentScope'] = parentScope;
    }

    private getParentScope(scope: Object): Object {
        var parentScope = scope['parentScope'];
        if (typeof parentScope !== 'undefined') {
            return parentScope;
        }
        return scope;
    }

    private interpretPattern(interpretedVariableName: string, pattern: string, variables: Object, actualScopeObject: Object, continueOnNullValue: boolean, replacementForNullValue: string): string {
        if (typeof actualScopeObject === 'undefined') {
            console.error("'" + interpretedVariableName + "' has an undefined scope");
            return "";
        }

        var foundNullValue: boolean;

        // DONE: `pattern` could be null
        // NOPE: so maybe use a construction like 'pattern ?? ""' here
        //       (not in callers because there are multiple)
        // DONE: or simply bail out
        if (pattern === null) {
            return null;
        }

        var interpretedpattern = pattern.replace(/{(.+?)}/g, (foundPattern, varName: string) => {
            if (!continueOnNullValue && foundNullValue) return "";
            var result: string;
            if (varName.indexOf("$") === 0) { //is config variable
                if (varName === "$this") {
                    result = <string>actualScopeObject; //if config variable is this, return this as value (can be used for primitive types like array of numbers or strings)

                // these are aliases of variables in the context of interpretScript
                // for renderBibliographyLine and renderReactComponent
                } else if (varName === "$data") {
                    //  XXX: this object still contains PascalCase keys incompatible
                    //       with the current BibliographyLine (modeled for dictionaries)
                    // return 'actualScopedObject'; // yes, with a 'd'
                    // NOTE: this one is transformed using _convertToCamelCase
                    return 'camelCaseData';

                } else if (varName === "$pascalCaseData") {
                    //  XXX: necessary for a roundtrip back to/through
                    //       list_configuration.json in makeBottomPanelAsync
                    //       (load-detail-url refers to "BookId")
                    return 'pascalCaseData';

                } else if (varName === "$elements") {
                    return 'this._elements';

                } else if (varName === "$constants") {
                    return 'this.constants';

                } else if (varName === "$baseUrl") {
                    result = getBaseUrl();
                } else {
                    result = this.interpretConfigurationVariable(varName, variables, actualScopeObject);
                }
            } else if (varName.indexOf("@") === 0) {
                result = localization.translate(varName.substring(1), "BibliographyModule").value;
            }
            else {
                result = actualScopeObject[varName];
                var tmpScope = actualScopeObject;
                while (typeof result === 'undefined' && this.getParentScope(tmpScope) !== tmpScope) { //if result is undefined bubble up to outer scope
                    tmpScope = this.getParentScope(tmpScope);
                    result = tmpScope[varName];
                }
            }

            if (typeof result !== 'undefined' && result !== null) {
                result = String(result); //convert if typeof result was not string
                if (result.length > 0) {
                    return result;
                }
            }
            foundNullValue = true;
            if (!continueOnNullValue) return "";
            return replacementForNullValue;
        });
        if (foundNullValue && !continueOnNullValue) {
            return null;
        }
        return interpretedpattern;
    }

    private interpretConfigurationVariable(varName: string, variables: Object, actualScopeObject: Object): string {
        if (typeof variables === 'undefined') {
            console.error("No variables are specified in bibliography configuration");
            return "";
        }
        if (typeof actualScopeObject === 'undefined') {
            console.log("Variable '" + varName + "' has null scope object");
            return "";
        }

        var interpretedVariable = variables[varName];
        if (typeof interpretedVariable === 'undefined') {
            console.error("Variable with name " + varName + " is not specidfied in bibliography configuration");
            return "";
        }

        var typeOfVariable = interpretedVariable['type'];
        if (typeof typeOfVariable === "undefined") {
            console.error("Variable with name " + varName + " does not have specified type");
            return "";
        }

        switch (typeOfVariable) {
        case "basic":
            return this.interpretBasic(varName, interpretedVariable, variables, actualScopeObject);
        case "replace":
            return this.interpretReplace(varName, interpretedVariable, variables, actualScopeObject);
        case "if":
            return this.interpretIfStatement(varName, interpretedVariable, variables, actualScopeObject);
        case "array":
            return this.interpretArray(varName, interpretedVariable, variables, actualScopeObject);
        case "table":
            return this.interpretTable(varName, interpretedVariable, variables, actualScopeObject);
        case "script":
            return this.interpretScript(varName, interpretedVariable, variables, actualScopeObject);
        default:
            console.error("Variable with name " + varName + " does not have correct type");
            return "";
        }
    }

    private interpretBasic(varName: string, interpretedVariable: Object, variables: Object, scopedObject: Object): string {
        var printIfNull: boolean = interpretedVariable["printIfNullValue"];
        // XXX: "Localized string with key=undefined was not found in dictionary=BibliographyModule with culture=undefined"
        //      should be fixed here by not passing `undefined` further (to lib/scalesoft-localization-web/dist/localization.ts)
        // XXX: in ITJakub.Web.Hub/wwwroot/content/BibliographyConfiguration/configuration.json,
        //      Grammar/middle-panel/variables/$printAuthors lacks replaceNullValueBy, unlike $LiteraryTypeStart, for instance

        var replacementForNullValue: string = "";
        if (interpretedVariable["replaceNullValueBy"]) {
            replacementForNullValue = localization.translate(interpretedVariable["replaceNullValueBy"], "BibliographyModule").value;

        } else {
            console.log(`${varName} is missing a "replaceNullValueBy" property, resorting to an empty string.`);

        }

        var pattern: string = interpretedVariable["pattern"];
        var actualScopedObject = this.resolveScope(interpretedVariable, scopedObject);
        var value: string = this.interpretPattern(varName, pattern, variables, actualScopedObject, printIfNull, replacementForNullValue);
        if ((value === null || value.length <= 0) && !printIfNull) {
            return "";
        } else {
            return value;
        }
    }

    private interpretReplace(varName: string, interpretedVariable: Object, variables: Object, scopedObject: Object): string {
        var replacing: string = interpretedVariable["replacing"];
        var replacement: string = interpretedVariable["replacement"];
        var pattern: string = interpretedVariable["pattern"];
        var actualScopedObject = this.resolveScope(interpretedVariable, scopedObject);
        var value: string = this.interpretPattern(varName, pattern, variables, actualScopedObject, true, "");
        return value.replace(new RegExp(replacing, 'g'), replacement);
    }

    private interpretIfStatement(varName: string, interpretedVariable: Object, variables: Object, scopedObject: Object): string {
        var pattern: string = interpretedVariable["pattern"];
        var actualScopedObject = this.resolveScope(interpretedVariable, scopedObject);
        var truePattern: string = interpretedVariable["onTrue"];
        var falsePattern: string = interpretedVariable["onFalse"];
        var patternResult = this.interpretPattern(varName, pattern, variables, actualScopedObject, true, "");
        if (typeof patternResult === "undefined" || patternResult === null || patternResult === "" || patternResult === "false" || patternResult === "0") {
            return this.interpretPattern(varName, falsePattern, variables, actualScopedObject, true, "");
        }
        return this.interpretPattern(varName, truePattern, variables, actualScopedObject, true, "");
    }

    private interpretArray(varName: string, interpretedVariable: Object, variables: Object, scopedObject: Object): string {
        var pattern: string = interpretedVariable["pattern"];
        var delimeter: string = interpretedVariable["delimeter"];
        var actualScopedObject = this.resolveScope(interpretedVariable, scopedObject);
        if (!$.isArray(actualScopedObject)) {
            console.error("Variable with name " + varName + " must be scoped on array.");
            return "";
        }
        var value: string = "";
        var actualScopedObjectList = actualScopedObject as Array<Object>;
        $.each(actualScopedObjectList, (index: number, item: Object) => {
            if (typeof item === 'undefined' || item === null) {
                return true; //continue
            }
            //  XXX: we cannot set a parentScope on a plain string; for this,
            //       the SearchResultDetailExtendedContract maps lists of plain
            //       string to objects that wrap them
            this.setParentScope(item, actualScopedObject);
            var itemValue = this.interpretPattern(varName, pattern, variables, item, true, "");
            if (index > 0) {
                itemValue = delimeter + itemValue;
            }
            value += itemValue;
        });

        return value;

    }

    private interpretTable(varName: string, interpretedVariable: Object, variables: Object, scopedObject: Object): string {
        var printRowIfNullValue: boolean = interpretedVariable["printRowIfNullValue"];
        var replaceNullValueBy: string = interpretedVariable["replaceNullValueBy"];
        if (!replaceNullValueBy)
            replaceNullValueBy = '';

        if (replaceNullValueBy)
            replaceNullValueBy = localization.translate(replaceNullValueBy, "BibliographyModule").value;

        var rows: Array<Object> = interpretedVariable["rows"];
        var actualScopedObject = this.resolveScope(interpretedVariable, scopedObject);
        var tableBuilder = new TableBuilder();

        $.each(rows, (index, item) => {
            var label: string = localization.translate(item["label"], "BibliographyModule").value;
            var pattern: string = item["pattern"];
            var value: string = this.interpretPattern(varName, pattern, variables, actualScopedObject, true, "");
            if (typeof value !== 'undefined' && value !== null && value.length > 0) {
                tableBuilder.makeTableRow(label, value);
            } else {
                if (printRowIfNullValue) {
                    tableBuilder.makeTableRow(label, replaceNullValueBy);
                }
            }
        });

        return tableBuilder.build().outerHTML;
    }

    private interpretScript(varName: string, interpretedVariable: Object, variables: Object, scopedObject: Object): string {
        var printIfNull: boolean = interpretedVariable["printIfNullValue"];
        var replaceNullValueBy: string = localization.translate(interpretedVariable["replaceNullValueBy"], "BibliographyModule").value;
        var pattern: string = interpretedVariable["pattern"];
        var actualScopedObject = this.resolveScope(interpretedVariable, scopedObject);
        const pascalCaseData = actualScopedObject; // PascalCase (see GetBookDetailInfo)
        const camelCaseData = this._convertToCamelCase(pascalCaseData);
        var interpretedScript = this.interpretPattern(varName, pattern, variables, actualScopedObject, false, "");
        var patternResult = eval(interpretedScript);
        if (typeof patternResult === "undefined" || patternResult === null || patternResult === "" || patternResult === "false" || patternResult === "0") {
            if (printIfNull) {
                if (typeof replaceNullValueBy !== "undefined" || replaceNullValueBy !== null) {
                    return replaceNullValueBy;
                } else {
                    return patternResult;
                }

            } else {
                return "";
            }
        }

        return patternResult;
    }

    private _convertToCamelCase(source)
    {
        if (Array.isArray(source)) {
            return source.map(value => this._convertToCamelCase(value));
        }

        //  XXX: also an "object" type
        if (source === null) {
            return null;
        }

        if (typeof source === 'object') {
            const keys = Object.keys(source);
            const ret = {};

            for (let origKey of keys) {
                //  XXX: definitely skip this to avoid running ad infinitum!
                if (origKey.toLowerCase() == 'parentscope')
                    continue;

                //  XXX: and this too! (NOPE: but do copy the reference)
                if (origKey.toLowerCase() == '_bibliographyfactory') {
                    // ret[origKey] = source[origKey];
                    continue;
                }

                let key = origKey;
                const value = this._convertToCamelCase(source[origKey]);

                if (key && key.length) {
                    key = key[0].toLowerCase() + key.slice(1);
                }

                ret[key] = value;
            }

            return ret;
        }

        return source;
    }
}

class TableBuilder {
    private m_tableDiv: HTMLDivElement;

    constructor() {
        this.m_tableDiv = document.createElement('div');
        $(this.m_tableDiv).addClass('bib-table');
    }

    public makeTableRow(label: string, value: string): void {
        var rowDiv: HTMLDivElement = document.createElement('div');
        $(rowDiv).addClass('bib-table-row');
        var labelDiv: HTMLDivElement = document.createElement('div');
        $(labelDiv).addClass('bib-table-cell bib-label');
        labelDiv.innerHTML = label;
        rowDiv.appendChild(labelDiv);
        var valueDiv: HTMLDivElement = document.createElement('div');
        $(valueDiv).addClass('bib-table-cell');
        valueDiv.innerHTML = value;
        rowDiv.appendChild(valueDiv);
        this.m_tableDiv.appendChild(rowDiv);
    }

    public build(): HTMLDivElement {
        return this.m_tableDiv;
    }
}
